<template>
  <section id="contact-us" class="text-white">
    <!-- Simple Success Popup -->
    <div v-show="showSuccess" class="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div class="fixed inset-0 bg-black/70"></div>
      <div class="relative bg-[#1a2820] p-6 rounded-lg text-center max-w-sm w-full">
        <i class="material-icons text-green-500 text-4xl mb-4">✔</i>
        <h3 class="text-xl text-[#E5C4A8] mb-2">Message Sent!</h3>
        <p class="text-[#E5C4A8]/80">We will get back to you shortly.</p>
      </div>
    </div>

    <div class="container">
      <!-- Header -->
      <div class="contact-header" data-aos="fade-down">
        <h2>CONTACT US</h2>
      </div>

      <!-- Links -->
      <div class="contact-wrapper">
        <div class="contact-grid">
          <!-- Form -->
          <div class="form-section" data-aos="fade-right">
            <!-- Error Message -->
            <div v-if="showError" class="error-message">
            <i class="fas fa-exclamation-circle"></i>
            Failed to send message. Please try again later.
             </div>

            <form @submit.prevent="handleSubmit" class="contact-form">
              <!-- Name Input -->
              <div class="input-group">
                <div class="input-icon">
                  <i class="fas fa-user"></i>
                </div>
                <input 
                  type="text" 
                  name="name"
                  placeholder="Your Name"
                  required
                  class="form-input"
                />
              </div>

              <!-- Email Input -->
              <div class="input-group">
                <div class="input-icon">
                  <i class="fas fa-envelope"></i>
                </div>
                <input 
                  type="email" 
                  name="email"
                  placeholder="Your Email"
                  required
                  class="form-input"
                />
                </div>

              <!-- Message Textarea -->
              <div class="input-group">
                <textarea 
                  name="message" 
                  placeholder="Your Message"
                  rows="6" 
                  required
                  class="form-textarea"
                ></textarea>
              </div>

              <!-- Submit Button -->
              <button
                type="submit" 
                class="submit-button"
              >
                <span v-if="!isSubmitting">
                  <i class="fas fa-paper-plane"></i> SEND MESSAGE
                </span>
                <span v-else>
                  <i class="fas fa-spinner fa-spin"></i> SENDING...
                </span>
              </button>
            </form>
          </div>

          <!-- Links -->
          <div class="info-section" data-aos="fade-left">
            <div class="illustration-wrapper">
              <div class="contact-illustration">
                <img src="../assets/images/logo.webp" alt="Revest360 Logo" class="logo-image" />
              </div>
              <h3>Get In Touch</h3>
            </div>

            <div class="social-links">
              <a
                v-for="(social, index) in socialLinks"
                :key="social.name"
                :href="social.url"
                target="_blank"
                rel="noopener noreferrer"
                class="social-link"
                :title="social.name"
                data-aos="zoom-in"
                :data-aos-delay="100 + (index * 50)"
              >
                <i :class="social.icon"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref } from "vue";
import { useContactForm } from '../hooks/useContactForm.js';
import { useAos } from '../hooks/useAos';

const { isSubmitting, showError, showSuccess, handleSubmit } = useContactForm();

// Social media links
const socialLinks = ref([
  {
    name: 'Twitter',
    icon: 'fa-brands fa-x-twitter',
    url: 'https://x.com'
  },
  {
    name: 'Instagram',
    icon: 'fab fa-instagram',
    url: 'https://instagram.com'
  },
  {
    name: 'LinkedIn',
    icon: 'fab fa-linkedin',
    url: 'https://linkedin.com'
  },
  {
    name: 'Facebook',
    icon: 'fab fa-facebook',
    url: 'https://facebook.com'
  },
  {
    name: 'YouTube',
    icon: 'fab fa-youtube',
    url: 'https://youtube.com'
  }
]);



useAos();
</script>

<style scoped>
  #contact-us {
    padding: 40px 20px;
  }

  .container {
    max-width: 1200px;
    margin: 0 auto;
    width: 100%;
  }

  .contact-header {
    text-align: center;
    margin-bottom: 40px;
    animation: fadeInDown 0.8s ease-in-out;
  }

  .contact-header h2 {
    font-size: 42px;
    font-weight: 700;
    color: #e4ac3b;
    margin-bottom: 12px;
    text-transform: uppercase;
    letter-spacing: 2px;
    text-shadow: 0 0 10px rgba(228, 172, 59, 0.5)
  }

  .contact-header p {
    font-size: 18px;
    color: #b0b0b0;
    font-weight: 400;
  }

  .contact-wrapper {
    animation: fadeInUp 0.8s ease-in-out;
  }

  .contact-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 60px;
    background: #111111;
    padding: 50px;
    box-shadow: 7px 7px 14px #0a0a0a, -7px -7px 14px #131313;
  }

  .form-section {
    display: flex;
    flex-direction: column;
  }

 .error-message {
    display: flex;
    padding: 12px 16px;
    margin-bottom: 20px;
    align-items: center;
    gap: 10px;
    background: rgba(255, 59, 48, 0.1);
    border: 1px solid #ff3b30;
    border-radius: 8px;
    color: #ff3b30;
    font-size: 14px;
  }

 .contact-form {
   display: flex;
   flex-direction: column;
   gap: 20px;
  }
 
 .input-group {
   position: relative;
  }

 .input-icon {
    position: absolute;
    left: 16px;
    top:50%; 
    color: #e4ac3b;
    font-size: 18px;
    pointer-events: none;
    transform: translateY(-50%);
    z-index: 1;
  }

 .input-group:has(textarea) .input-icon {
    top: 20px;
    transform: none;
  }

  .form-input,
  .form-textarea {
    width: 100%;
    background: #0a0a0a;
    color: #e0e0e0;
    border: 1px solid #2a2a2a;
    border-radius: 10px;
    padding: 14px 16px 14px 48px;
    font-size: 16px;
    transition: all 0.3s ease;
    outline: none;
  }

  .form-textarea {
    resize: none;
    padding: 14px 16px;
    font-family: inherit;
  }

  .form-input::placeholder,
  .form-textarea::placeholder {
    color: #666;
  }

  .form-input:-webkit-autofill,
  .form-input:-webkit-autofill:hover,
  .form-input:-webkit-autofill:focus {
    -webkit-text-fill-color: #e0e0e0;
    -webkit-box-shadow: 0 0 0px 1000px #0a0a0a inset;
    transition: background-color 5000s ease-in-out 0s;
  }

  .submit-button {
    background: linear-gradient(135deg, #e4ac3b 0%, #FF6B00 100%);
    color: #111111;
    border: none;
    padding: 16px 32px;
    border-radius: 10px;
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.3s ease;
    text-transform: uppercase;
    letter-spacing: 1px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
  }

  .submit-button:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 0 25px rgba(228, 172, 59, 0.8);
  }

  .submit-button:active:not(:disabled) {
    transform: scale(0.98);
  }
  
  .submit-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
   .info-section {
   display: flex;
   flex-direction: column;
   justify-content: space-between;
   align-items: center;
 }
 
 .illustration-wrapper {
   text-align: center;
   margin-bottom: 40px;
 }
 
 .contact-illustration {
   width: 200px;
   height: 200px;
   border-radius: 50%;
   display: flex;
   align-items: center;
   justify-content: center;
   margin: 0 auto 30px;
   padding: 30px;
   animation: float 6.7s ease-in-out infinite;
   overflow: hidden;
 }

 .logo-image {
   width: 100%;
   height: 100%;
   object-fit: contain;
   filter: brightness(1.2) drop-shadow(0 0 15px rgba(228, 172, 59, 0.6));
 }
 
 .illustration-wrapper h3 {
    font-size: 28px;
    font-weight: 700;
    color: #e4ac3b;
    margin-bottom: 12px;
  }
  
  .illustration-wrapper p {
    font-size: 16px;
    color: #b0b0b0;
    line-height: 1.6;
    max-width: 300px;
    margin: 0 auto;
  }
  
  .social-links {
    display: flex;
    gap: 20px;
    flex-wrap: wrap;
    justify-content: center;
  }
  
  .social-link {
    width: 50px;
    height: 50px;
    background: #0a0a0a;
    border: 1px solid #2a2a2a;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #e4ac3b;
    font-size: 22px;
    transition: all 0.3s ease;
    cursor: pointer;
  }
  
  .social-link:hover {
    background: linear-gradient(135deg, #e4ac3b 0%, #FF6B00 100%);
    color: #111111;
    transform: translateY(-5px) rotate(5deg);
    box-shadow: 0 0 20px rgba(228, 172, 59, 0.8);
    border-color: transparent;
  }
  
  .social-link:active {
    transform: scale(0.95);
  }
  
  .success-icon {
    font-size: 60px;
    color: #e4ac3b;
    margin-bottom: 20px;
    animation: scaleIn 0.5s ease-in-out;
  }

  /* Animations */
  @keyframes fadeInDown {
    from {
      opacity: 0;
      transform: translateY(-30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes float {
    0%, 100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-20px);
    }
  }
  
  @keyframes scaleIn {
    from {
      transform: scale(0);
    }
    to {
      transform: scale(1);
    }
  }
  
  /* Mobile Responsiveness */
  @media (max-width: 968px) {
    .contact-grid {
      grid-template-columns: 1fr;
      gap: 40px;
      padding: 40px 30px;
    }
  
    .info-section {
      order: -1;
    }
  }
  
  @media (max-width: 768px) {
    #contact-us {
      padding: 60px 15px;
    }
  
    .contact-header h2 {
      font-size: 32px;
    }
  
    .contact-header p {
      font-size: 16px;
    }
  
    .contact-grid {
      padding: 30px 20px;
    }
  
    .contact-illustration {
      width: 160px;
      height: 160px;
      padding: 25px;
    }
  
    .illustration-wrapper h3 {
      font-size: 24px;
    }
  
    .illustration-wrapper p {
      font-size: 14px;
    }
  
    .social-link {
      width: 45px;
      height: 45px;
      font-size: 20px;
    }
  }
  
  @media (max-width: 480px) {
    #contact-us {
      padding: 40px 10px;
    }
  
    .contact-header h2 {
      font-size: 24px;
    }
  
    .contact-header p {
      font-size: 14px;
    }
  
    .contact-grid {
      padding: 25px 15px;
    }
  
    .form-input,
    .form-textarea {
      font-size: 14px;
      padding: 12px 14px 12px 44px;
    }
  
    .submit-button {
      font-size: 14px;
      padding: 14px 28px;
    }
  
    .contact-illustration {
      width: 130px;
      height: 130px;
      padding: 20px;
    }
  
    .illustration-wrapper h3 {
      font-size: 20px;
    }
  
    .social-links {
      gap: 15px;
    }
  
    .social-link {
      width: 40px;
      height: 40px;
      font-size: 18px;
    }
  }
</style>
